/**
 * Cliente de suscripción SaaS (Recurrente) — alineado con PLAN-SUSCRIPCIONES-PAGOS-Y-LOGIN.
 */
import { session } from './auth.svelte';

export type BillingPlan = {
  slug: string;
  nombre: string;
  precioMensualGtq: number;
  limiteUsuarios?: number;
};

export type BillingStatus = {
  tenantId: string;
  estado: string;
  trialEndsAt: string | null;
  diasRestantesTrial: number | null;
  plan: BillingPlan | null;
  paymentsEnabled: boolean;
  checkoutAvailable: boolean;
  contactoWhatsApp: string;
};

export const PAYMENTS_ENABLED = import.meta.env.VITE_PAYMENTS_ENABLED === 'true';
export const SHOW_TRIAL_BANNER = import.meta.env.VITE_SHOW_TRIAL_BANNER !== 'false';
export const WHATSAPP_LINK = 'https://wa.me/50237863061';

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { Accept: 'application/json' };
  const s = session.value;
  if (s?.token) {
    h.Authorization = `Bearer ${s.token}`;
  }
  return h;
}

export async function fetchBillingStatus(): Promise<BillingStatus | null> {
  const s = session.value;
  if (!s?.token) return null;
  const r = await fetch('/api/auth/v1/tenant/billing-status', { headers: authHeaders() });
  if (!r.ok) return null;
  return r.json();
}

export type CheckoutResult = { checkoutId: string; url: string };

export async function startSubscriptionCheckout(
  tenantId: string,
  plan: BillingPlan,
  customerEmail?: string,
): Promise<CheckoutResult> {
  const origin = typeof location !== 'undefined' ? location.origin : '';
  const body = {
    tenantId,
    planSlug: plan.slug,
    planName: plan.nombre,
    amount: plan.precioMensualGtq,
    currency: 'GTQ',
    successUrl: `${origin}/billing/success`,
    cancelUrl: `${origin}/billing/cancel`,
    customer: customerEmail
      ? { email: customerEmail, firstName: '', lastName: '', phone: '' }
      : undefined,
  };
  const r = await fetch('/api/payments/v1/payments/checkouts/subscription', {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (r.status === 503) {
    const err = await r.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error === 'payments-disabled' ? 'payments-disabled' : 'pagos-no-disponibles');
  }
  if (!r.ok) {
    const txt = await r.text().catch(() => '');
    throw new Error(txt.slice(0, 200) || `checkout-${r.status}`);
  }
  const data = await r.json() as { checkoutId?: string; url?: string };
  if (!data.url) throw new Error('checkout-sin-url');
  return { checkoutId: data.checkoutId ?? '', url: data.url };
}

export function trialUrgency(dias: number | null | undefined): 'none' | 'info' | 'warn' | 'critical' | 'expired' {
  if (dias == null) return 'none';
  if (dias < 0) return 'expired';
  if (dias <= 3) return 'critical';
  if (dias <= 7) return 'warn';
  return 'info';
}
